/**
 * Parse e build de parâmetros de querystring para formulário de alerta.
 * Contrato de URL: /alertas/novo?origin=SAO&destination=MIA&departure=...&return=...&open-dates=1&price=...&source=...
 */

import { getAirports } from "shared/services/contexts/airport.service";
import type { Airport } from "shared/types/airportsTypes";

export interface AlertPrefill {
  origin: Airport | null;
  destination: Airport | null;
  departureDates: string[];
  returnDates: string[];
  openDates: boolean;
  priceCents: number;
  source: string | null;
}

/**
 * Faz parse de um intervalo de datas separadas por vírgula em formato YYYY-MM-DD.
 * Remove datas inválidas e passadas (< hoje, horário local).
 * Retorna [min] se sobrar uma, [min, max] se duas, [] se nenhuma.
 */
function parseDateRange(dateStr: string | null, today: Date): string[] {
  if (!dateStr) return [];

  const todayStr = today.toISOString().split("T")[0];

  const dates = dateStr
    .split(",")
    .map((d) => d.trim())
    .filter((d) => {
      // Valida formato YYYY-MM-DD
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
      // Remove datas passadas (< hoje)
      return d >= todayStr;
    });

  // Remove duplicatas e ordena
  const unique = Array.from(new Set(dates)).sort();

  if (unique.length === 0) return [];
  if (unique.length === 1) return [unique[0]];
  return [unique[0], unique[unique.length - 1]];
}

/**
 * Faz parse completo da querystring em um objeto AlertPrefill.
 */
export function parseAlertPrefill(
  search: string,
  today: Date = new Date(),
): AlertPrefill {
  const params = new URLSearchParams(search);
  const airports = getAirports();

  const originCode = params.get("origin")?.toUpperCase();
  const destinationCode = params.get("destination")?.toUpperCase();

  const origin = originCode ? airports.findAirportByCode(originCode) : null;
  const destination = destinationCode
    ? airports.findAirportByCode(destinationCode)
    : null;

  const openDates =
    params.get("open-dates") === "true" || params.get("open-dates") === "1";

  const departureDates = openDates
    ? []
    : parseDateRange(params.get("departure"), today);
  let returnDates = openDates
    ? []
    : parseDateRange(params.get("return"), today);

  // Descarta return se for anterior à departure
  if (
    returnDates.length > 0 &&
    departureDates.length > 0 &&
    returnDates[0] < departureDates[0]
  ) {
    returnDates = [];
  }

  // Parse de preço em reais
  const priceStr = params.get("price");
  let priceCents = 0;
  if (priceStr) {
    const price = parseFloat(priceStr);
    if (price > 0) {
      priceCents = Math.round(price * 100);
    }
  }

  // Valida source: [a-z0-9_-]{1,40}
  let source: string | null = params.get("source");
  if (source && !/^[a-z0-9_-]{1,40}$/.test(source)) {
    source = null;
  }

  return {
    origin,
    destination,
    departureDates,
    returnDates,
    openDates,
    priceCents,
    source,
  };
}

/**
 * Constrói uma URL de criação de alerta com parâmetros pré-preenchidos.
 * Parâmetros vazios são omitidos. Datas ordenadas no intervalo [min, max].
 */
export function buildAlertCreateUrl(input: {
  origin: string;
  destination: string;
  departureDates?: string[];
  returnDates?: string[];
  source?: string;
}): string {
  const params = new URLSearchParams();

  if (input.origin) {
    params.set("origin", input.origin.toUpperCase());
  }

  if (input.destination) {
    params.set("destination", input.destination.toUpperCase());
  }

  const departureDates = (input.departureDates ?? [])
    .filter((d) => typeof d === "string")
    .sort();
  if (departureDates.length > 0) {
    params.set("departure", departureDates.join(","));
  }

  const returnDates = (input.returnDates ?? [])
    .filter((d) => typeof d === "string")
    .sort();
  if (returnDates.length > 0) {
    params.set("return", returnDates.join(","));
  }

  if (input.source) {
    params.set("source", input.source);
  }

  const query = params.toString();
  return query ? `/alertas/novo?${query}` : "/alertas/novo";
}

/**
 * Determina a origem de entrada do formulário baseado no prefill.
 */
export function getAlertEntrySource(prefill: AlertPrefill): string {
  if (prefill.source) {
    return prefill.source;
  }

  const hasAnyPrefill =
    prefill.origin ||
    prefill.destination ||
    prefill.departureDates.length > 0 ||
    prefill.returnDates.length > 0 ||
    prefill.priceCents > 0;

  return hasAnyPrefill ? "link" : "direct";
}
